import { GzTooltipWebComponent } from '../../../ui/gzTooltip';
import { GzIconLabelWebComponent } from '../../../ui/gzIconLabel';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { ActionsColumnValues, DEVICES_CLASS, PROVIDER_STR_CLASS, State } from '../../common/constants';
import { formatMacAddress, isCloudResource } from '../../common/utils';
import { html } from 'lit';
import { translateFn } from '../../../utils/translator.helper';
import { RiskManagementSideMenuEvents } from '../../sideMenus/common/common';
import { osRenderer, selectColumnRenderer } from '../../common/customRenderers';
import { FontSize } from '../../../ui/gzIconButton/types';
import { entityIconSet } from '../../../theme/entity-icons';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';

export function endpointNameRenderer(rowData) {
  return html`<span class="cell-label">${rowData?.item?.endpointName}</span>`;
}

export function deviceTagsRenderer(rowData) {
  if (!rowData?.item?.tags || rowData?.item?.tags.length === 0) {
    return '-';
  }

  return html`<span class="cell-label"> ${rowData?.item?.tags.join(', ')} </span>`;
}

export function deviceMacsRenderer(rowData) {
  if (!rowData?.item?.macs || rowData?.item?.macs.length === 0) {
    return '-';
  }
  return html`<span class="cell-label"> ${rowData?.item?.macs.map(mac => formatMacAddress(mac)).join(', ')} </span>`;
}

export function deviceMisconfigurationsRenderer(rowData) {
  return html`<span class="cell-label"> ${rowData?.item?.misconfigurationIds.length} </span>`;
}

export function deviceTypeRenderer(rowData) {
  const data = new GzTooltipWebComponent();
  data.classList.add('cell-label');
  data.classList.add('list-buttons');
  data.setAttribute('part', 'item-group-icons');

  const tooltip = BaseWebComponentTranslator.getInstance().getDeviceTypes(rowData?.item?.endpointType);
  const gzIcon = new GzIconLabelWebComponent();
  gzIcon.setCustomIconSet(entityIconSet);
  gzIcon.size = FontSize.ICON_SIZE_20;
  gzIcon.icon = DEVICES_CLASS.get(rowData?.item?.endpointType);
  gzIcon.setLabelColor('var(--body-text-arrows-400)');
  gzIcon.iconColor = 'var(--chart-300-icon-buttons)';
  data.appendChild(gzIcon);
  data.tooltipText = tooltip;

  return data;
}

export function actionsColumnRenderer(rowData) {
  if (!rowData.item.company.specifics.features.iorUsable) {
    return selectColumnRenderer.call(this, rowData, []);
  }

  const options = [] as Array<any>;

  options.push({
    title: translateFn('Fix risks'),
    value: ActionsColumnValues.FIX_RISK,
    event: {
      name: RiskManagementSideMenuEvents.FIX_MISCONFIGURATIONS_INLINE,
      detail: {
        companyId: rowData.item.company._id,
        endpointsIds: [rowData.item.endpointId],
        hasFrat: rowData.item.hasFrat,
      },
    },
  });

  options.push({
    title: translateFn('Roll back fixes'),
    value: ActionsColumnValues.ROLLBACK_FIX,
    event: {
      name: RiskManagementSideMenuEvents.ROLLBACK_DEVICES_INLINE,
      detail: {
        endpointId: rowData.item.endpointId,
        endpointName: rowData.item.endpointName,
        companyId: rowData.item.company._id,
        hasFrat: rowData.item.hasFrat,
      },
    },
  });

  if (rowData.item.state === State.IGNORED) {
    options.push({
      title: translateFn('Restore ignored resource'),
      value: ActionsColumnValues.RESTORE,
      event: {
        name: RiskManagementSideMenuEvents.RESTORE_DEVICES,
        detail: {
          companyId: rowData.item.company._id,
          endpointsIds: [rowData.item.endpointId],
        },
      },
    });
  } else {
    options.push({
      title: translateFn('Ignore resource'),
      value: ActionsColumnValues.IGNORE,
      event: {
        name: RiskManagementSideMenuEvents.IGNORE_DEVICES,
        detail: {
          companyId: rowData.item.company._id,
          endpointsIds: [rowData.item.endpointId],
        },
      },
    });
  }

  if (!isCloudResource(rowData.item.endpointType)) {
    const isolateOption = {
      value: ActionsColumnValues.ISOLATE_DEVICE,
      title: rowData.item.isIsolated ? translateFn('Remove from isolation') : translateFn('Isolate'),
      event: {
        name: RiskManagementSideMenuEvents.ISOLATE_DEVICE_INLINE_MENU,
        detail: {
          companyId: rowData.item.company._id,
          endpointId: rowData.item.endpointId,
          appId: rowData.item.appId,
        },
      },
    };

    if (!rowData.item.company.specifics.features.isolationUsable) {
      isolateOption['disabled'] = true;
    }

    options.push(isolateOption);
  }

  return selectColumnRenderer.call(this, rowData, options);
}

export function cloudAccountRenderer(rowData) {
  return html`<span class="cell-label">${rowData.item.cloudAccount || '-'}</span>`;
}

export function repositoryNameRenderer(rowData) {
  return html`<span class="cell-label">${rowData.item.imageMetadata?.imageRepo || '-'}</span>`;
}

export function registryHostRenderer(rowData) {
  if (!rowData.item.imageMetadata?.registryHost) {
    return html`<span class="cell-label">-</span>`;
  }

  return html`
    <span class="cell-label">
      <div class="d-flex align-items-center" style="gap: 4px;">
        <gz-breach-path-graph-icon
          name="${PROVIDER_STR_CLASS.get(rowData.item.imageMetadata?.cloudProvider)}"
          color="var(--chart-302-icon-buttons-titles)"
        ></gz-breach-path-graph-icon>
        <gz-tooltip isEllipsis tooltipText="${rowData.item.imageMetadata.registryHost}">
          <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="d-inline">
            ${rowData.item.imageMetadata.registryHost}
          </gz-text>
        </gz-tooltip>
      </div>
    </span>
  `;
}

export function clusterNameRenderer(rowData) {
  return html`<span class="cell-label">${rowData.item.kspmMetadata?.clusterName || '-'}</span>`;
}
